import { cn } from '@design-system/helpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system/tooltip';
import { BrainIcon } from '@features/conversations/icons';

import type { KnowledgeBase } from '../services/knowledge-base-api';
import { agentsLabel } from '../utils/labels';

export function KbaseAvatar({
  className,
  active = true,
}: {
  className?: string;
  active?: boolean;
}) {
  return (
    <span
      data-slot='kbase-avatar'
      data-active={active}
      aria-hidden='true'
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-full',
        active
          ? 'bg-primary-100 text-primary-1000'
          : 'bg-gray-100 text-gray-400',
        className
      )}
    >
      <BrainIcon
        size={22}
        variant='Bold'
      />
    </span>
  );
}

export function KbaseRow({
  kbase,
  selected,
  collapsed,
  onSelect,
}: {
  kbase: KnowledgeBase;
  selected: boolean;
  /** Icon-only rendering while the column is folded away. */
  collapsed?: boolean;
  onSelect: (id: number) => void;
}) {
  return (
    <div
      role='option'
      tabIndex={0}
      data-slot='kbase-row'
      data-kbase-id={kbase.id}
      aria-selected={selected}
      aria-label={kbase.name}
      onClick={() => onSelect(kbase.id)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(kbase.id);
        }
      }}
      className={cn(
        'flex w-full cursor-pointer items-start px-3 py-2 text-left transition-colors',
        collapsed && 'justify-center px-0',
        selected ? 'bg-primary-30' : 'bg-white hover:bg-primary-30'
      )}
    >
      <div
        className={cn(
          'flex items-start gap-2 overflow-hidden p-3',
          collapsed ? 'justify-center' : 'w-full'
        )}
      >
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <KbaseAvatar active={kbase.is_active} />
              </span>
            </TooltipTrigger>
            <TooltipContent side='right'>{kbase.name}</TooltipContent>
          </Tooltip>
        ) : (
          <KbaseAvatar active={kbase.is_active} />
        )}
        {!collapsed && (
          <div className='flex min-w-0 flex-1 flex-col justify-center gap-1'>
            <span className='truncate text-base font-medium text-gray-900'>
              {kbase.name}
            </span>
            <span className='flex items-center gap-1 text-sm italic text-gray-600'>
              <span>{agentsLabel(kbase.ai_agents.length)}</span>
              <span aria-hidden='true'>•</span>
              <span
                data-slot='kbase-status'
                className={kbase.is_active ? 'text-[#00b637]' : 'text-red-500'}
              >
                {kbase.is_active ? 'Active' : 'Inactive'}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
